/**
 * MONSTER TOWN — COOKING CONTENT. DATA ONLY. Owned by the COOKING crew.
 *
 * Nothing in this file is engine. A new ingredient is an entry in `INGREDIENTS`,
 * a new recipe is an entry in `RECIPES`, and that is the whole change — the
 * kitchen sheet, the collection book, the errand engine and the zoo all read
 * them through `src/cooking/types.ts` and none of them is touched.
 *
 * ===========================================================================
 *  THE REFERENCE ERRAND'S HALF OF THE CONTRACT
 * ===========================================================================
 *   ing_fruit          — three of these, off the tree in the garden
 *   recipe_fruit_bowl  — the recipe, cooked in the kid's OWN kitchen
 *   dish_fruit_bowl    — what comes out, and what the zoo's trough wants
 * Those three ids are named in `src/content/errands.ts`, which is owned by
 * somebody else. They are load-bearing and must not be renamed.
 *
 * WHERE INGREDIENTS COME FROM (brief §5: "the shop, the garden, fishing, and
 * gifts"). This crew ships the GARDEN and the PANTRY, because those are the two
 * a kid can always reach with no coins and no other crew's system:
 *   the fruit tree   -> ing_fruit
 *   the veg patch    -> ing_veg
 *   the berry bush   -> ing_berry
 *   the pantry       -> ing_grain, ing_milk, ing_honey, in turn
 * Fishing catches and neighbour gifts arrive in the same bag by construction
 * (`inventory.add`), so those crews need to do nothing at all for their
 * ingredients to be cookable.
 *
 * NOTHING HERE CAN RUN OUT. Every source gives on every single tap, forever
 * (LAW #2). There is no stock, no cooldown longer than a double-tap debounce,
 * and no season.
 */

import { FOOT_1x1, FOOT_2x2 } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { defineCollectible, type CollectionEntry } from '../core/collection'
import {
  cookPotArt,
  fruitTreeArt,
  kitchenShelfArt,
  kitchenWindowArt,
  pantryArt,
  vegPatchArt,
} from '../cooking/art'
import {
  defineIngredient,
  defineRecipe,
  type IngredientDef,
  type RecipeDef,
} from '../cooking/types'

const id = (s: string): PlaceableId => s as PlaceableId

// ---------------------------------------------------------------------------
// ingredients
// ---------------------------------------------------------------------------

export const INGREDIENTS: readonly IngredientDef[] = [
  defineIngredient({
    id: 'ing_fruit',
    devName: 'Fruit',
    pic: 'fruit',
    color: 'flowerRed',
    accent: 'leafLight',
    from: 'garden',
    // Not "the garden" — THE TREE, the one standing inside the kid's own fence.
    source: 'tree',
  }),
  defineIngredient({
    id: 'ing_veg',
    devName: 'Vegetable',
    pic: 'veg',
    color: 'monsterCoral',
    accent: 'leaf',
    from: 'garden',
    source: 'patch',
  }),
  defineIngredient({
    id: 'ing_berry',
    devName: 'Berries',
    pic: 'berry',
    color: 'flowerPurple',
    accent: 'roofPurple',
    from: 'garden',
    source: 'bush',
  }),
  defineIngredient({
    id: 'ing_grain',
    devName: 'Grain',
    pic: 'grain',
    color: 'roofMustard',
    accent: 'woodLight',
    from: 'pantry',
  }),
  defineIngredient({
    id: 'ing_milk',
    devName: 'Milk',
    pic: 'milk',
    color: 'flowerWhite',
    accent: 'wallBlue',
    from: 'pantry',
  }),
  defineIngredient({
    id: 'ing_honey',
    devName: 'Honey',
    pic: 'honey',
    color: 'flowerYellow',
    accent: 'roofMustard',
    from: 'pantry',
  }),
]

/** What the pantry hands over, in order, cycling forever. */
export const PANTRY_CYCLE: readonly string[] = ['ing_grain', 'ing_milk', 'ing_honey']

// ---------------------------------------------------------------------------
// recipes — a picture, an order, and a count. That is the whole grammar.
// ---------------------------------------------------------------------------

/** THE REFERENCE RECIPE. Three fruit, one bowl, no heat, nothing to get wrong. */
export const fruitBowl = defineRecipe({
  id: 'recipe_fruit_bowl',
  devName: 'Fruit bowl',
  dish: 'dish_fruit_bowl',
  pic: 'bowl',
  color: 'flowerRed',
  accent: 'roofMustard',
  steps: [{ item: 'ing_fruit', count: 3 }],
  stirs: 3,
  coins: 12,
  feeds: 'hungry',
})

export const RECIPES: readonly RecipeDef[] = [
  fruitBowl,
  defineRecipe({
    id: 'recipe_pancakes',
    devName: 'Pancakes',
    dish: 'dish_pancakes',
    pic: 'pancake',
    color: 'woodLight',
    accent: 'roofMustard',
    steps: [
      { item: 'ing_grain', count: 2 },
      { item: 'ing_milk', count: 1 },
    ],
    stirs: 3,
    coins: 14,
    feeds: 'hungry',
  }),
  defineRecipe({
    id: 'recipe_veg_stew',
    devName: 'Vegetable stew',
    dish: 'dish_veg_stew',
    pic: 'stew',
    color: 'roofRed',
    accent: 'monsterCoral',
    steps: [
      { item: 'ing_veg', count: 2 },
      { item: 'ing_grain', count: 1 },
    ],
    stirs: 4,
    coins: 16,
    feeds: 'hungry',
  }),
  defineRecipe({
    id: 'recipe_berry_jam',
    devName: 'Berry jam',
    dish: 'dish_berry_jam',
    pic: 'jam',
    color: 'flowerPurple',
    accent: 'wallLilac',
    steps: [
      { item: 'ing_berry', count: 2 },
      { item: 'ing_honey', count: 1 },
    ],
    stirs: 3,
    coins: 16,
  }),
  defineRecipe({
    id: 'recipe_honey_cake',
    devName: 'Honey cake',
    dish: 'dish_honey_cake',
    pic: 'cake',
    color: 'flowerYellow',
    accent: 'wallPink',
    steps: [
      { item: 'ing_grain', count: 1 },
      { item: 'ing_honey', count: 1 },
      { item: 'ing_milk', count: 1 },
    ],
    stirs: 4,
    coins: 20,
  }),
  defineRecipe({
    id: 'recipe_berry_juice',
    devName: 'Berry juice',
    dish: 'dish_berry_juice',
    pic: 'juice',
    color: 'flowerPurple',
    accent: 'flowerPink',
    steps: [
      { item: 'ing_berry', count: 2 },
      { item: 'ing_fruit', count: 1 },
    ],
    stirs: 3,
    coins: 14,
    feeds: 'hungry',
  }),
]

/**
 * EVERY RECIPE IS A PAGE IN THE COLLECTION BOOK, derived rather than repeated —
 * so a recipe added above cannot forget to bring its page. `where: [{}]` is
 * "anywhere, any time", which is right for a recipe: it is discovered by
 * COOKING it, not by standing in the right place at the right hour.
 */
export const RECIPE_COLLECTIBLES: readonly CollectionEntry[] = RECIPES.map((r) =>
  defineCollectible({
    id: r.id,
    kind: 'recipe',
    devName: r.devName,
    icon: 'dinner',
    where: [{}],
    coins: r.coins ?? 10,
    itemId: r.dish,
  }),
)

// ---------------------------------------------------------------------------
// the places food comes from
// ---------------------------------------------------------------------------

/**
 * The tap verb every source uses: `cook:pick:<itemId>`. `src/cooking/kitchen.ts`
 * listens for it on `spawn:request` and puts the thing in the bag, exactly the
 * way the venue crew's stations use `venue:add:<itemId>`. The placeable stays
 * pure data and this file imports no bag, no errand engine and no HUD.
 */
export const fruitTree = definePlaceable<{ picked: number }>({
  id: id('cook_fruit_tree'),
  devName: 'Fruit tree',
  category: 'tree',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['flowerRed', 'roofMustard', 'flowerPink', 'flowerPurple'],
  tags: ['garden', 'starter', 'cooking', 'source'],
  initialState: () => ({ picked: 0 }),
  buildArt: (ctx) => fruitTreeArt(ctx, ctx.tint ?? 'flowerRed'),
  // LAW #1: the crown shakes, leaves come down, and a fruit lands in the bag.
  // It never runs out — a tree with nothing on it is a dead end (LAW #2).
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'shake', seconds: 0.55, target: 'crown' },
      { kind: 'anim', clip: 'bob', seconds: 0.5, target: 'fruit' },
      { kind: 'particles', effect: 'leaves', count: 7 },
      { kind: 'spawn', what: 'cook:pick:ing_fruit' },
      { kind: 'sound', cue: 'leaves_rustle' },
      { kind: 'state', patch: { picked: ctx.state.picked + 1 } },
    ],
    cooldownMs: 260,
  }),
})

export const vegPatch = definePlaceable<{ picked: number }>({
  id: id('cook_veg_patch'),
  devName: 'Vegetable patch',
  category: 'plant',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['monsterCoral', 'flowerYellow', 'monsterLime'],
  tags: ['garden', 'starter', 'cooking', 'source'],
  initialState: () => ({ picked: 0 }),
  buildArt: (ctx) => vegPatchArt(ctx, 'leafLight', ctx.tint ?? 'monsterCoral'),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'tops' },
      { kind: 'particles', effect: 'dustPuff', count: 6 },
      { kind: 'spawn', what: 'cook:pick:ing_veg' },
      { kind: 'sound', cue: 'leaves' },
      { kind: 'state', patch: { picked: ctx.state.picked + 1 } },
    ],
    cooldownMs: 260,
  }),
})

export const berryBush = definePlaceable<{ picked: number }>({
  id: id('cook_berry_bush'),
  devName: 'Berry bush',
  category: 'plant',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['flowerPurple', 'flowerRed', 'wallBlue'],
  tags: ['garden', 'cooking', 'source'],
  initialState: () => ({ picked: 0 }),
  buildArt: (ctx) => vegPatchArt(ctx, 'leafDark', ctx.tint ?? 'flowerPurple'),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'tops' },
      { kind: 'particles', effect: 'petals', count: 7 },
      { kind: 'spawn', what: 'cook:pick:ing_berry' },
      { kind: 'sound', cue: 'leaves_rustle' },
      { kind: 'state', patch: { picked: ctx.state.picked + 1 } },
    ],
    cooldownMs: 260,
  }),
})

/**
 * The store cupboard. Hands over grain, then milk, then honey, then grain
 * again, for ever — so all three staples are reachable by tapping one thing
 * repeatedly, which is what a three-year-old will actually do.
 */
export const pantry = definePlaceable<{ opens: number }>({
  id: id('cook_pantry'),
  devName: 'Pantry',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wallCream', 'wallMint', 'wallPink', 'wallBlue'],
  tags: ['starter', 'cooking', 'source'],
  initialState: () => ({ opens: 0 }),
  buildArt: (ctx) => pantryArt(ctx),
  interact: (ctx) => {
    const opens = ctx.state.opens + 1
    const item = PANTRY_CYCLE[opens % PANTRY_CYCLE.length] ?? 'ing_grain'
    return {
      feedback: [
        { kind: 'anim', clip: 'swing', seconds: 0.6, target: 'doorL' },
        { kind: 'anim', clip: 'jiggle', seconds: 0.5, target: 'jars' },
        { kind: 'particles', effect: 'sparkle', count: 6 },
        { kind: 'spawn', what: `cook:pick:${item}` },
        { kind: 'sound', cue: 'fridge_open' },
        { kind: 'state', patch: { opens } },
      ],
      cooldownMs: 300,
    }
  },
})

/**
 * THE COOKING POT — the one tap that opens the kitchen.
 *
 * `ui` feedback rather than a spawn verb, because opening a picture panel is
 * exactly what that feedback kind is for. `src/cooking/sheet.ts` answers the
 * `ui:panel` event; the HUD's own panel host does not know the name and
 * politely does nothing, which is why this needs no edit in `src/ui/**`.
 */
export const cookPot = definePlaceable<{ cooked: number }>({
  id: id('cook_pot'),
  devName: 'Cooking pot',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  // TEAL FIRST — the same teal as the pot painted on the cook sheet this thing
  // opens. A red pot that opens a screen with a teal pot on it is two pots.
  tints: ['roofTeal', 'roofRed', 'roofMustard', 'wallLilac'],
  tags: ['starter', 'cooking'],
  initialState: () => ({ cooked: 0 }),
  buildArt: (ctx) => cookPotArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'spin', seconds: 0.6, target: 'spoon' },
      { kind: 'particles', effect: 'steam', count: 6 },
      { kind: 'monster', action: 'cook', seconds: 1.1 },
      { kind: 'ui', panel: 'cook' },
      { kind: 'sound', cue: 'ladle' },
      { kind: 'state', patch: { cooked: ctx.state.cooked + 1 } },
    ],
    cooldownMs: 420,
  }),
})

/**
 * ===========================================================================
 *  THE WINDOW AND THE SHELF — the room catches up with the picture of it
 * ===========================================================================
 * The cook sheet paints a kitchen with a window (sky, hills, a sun) and a shelf
 * of jars with a pan hanging over it. The room a kid taps the pot in had a mint
 * box and some counters. Two placeables close that gap, and they are a DATA DROP
 * in the crew's own two files — that is the content litmus test, met.
 *
 * Both are free forever and both are seeded onto the kitchen's back wall by
 * `seedGardenAndKitchen()`. A kid can buy more from the drawer and hang them in
 * any room they like.
 */
export const kitchenWindow = definePlaceable<{ opened: number }>({
  id: id('cook_window'),
  devName: 'Kitchen window',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['flowerWhite', 'wallCream', 'wallBlue', 'wallPink'],
  tags: ['starter', 'cooking', 'decor'],
  initialState: () => ({ opened: 0 }),
  buildArt: (ctx) => kitchenWindowArt(ctx),
  // LAW #1: the panes swing, the day comes in as sparkles, and a bird sings
  // outside. Three visible things before a single sound is played.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'swing', seconds: 0.7, target: 'panes' },
      { kind: 'anim', clip: 'jiggle', seconds: 0.6, target: 'herbs' },
      { kind: 'particles', effect: 'sparkle', count: 8 },
      { kind: 'sound', cue: 'bird' },
      { kind: 'state', patch: { opened: ctx.state.opened + 1 } },
    ],
    cooldownMs: 320,
  }),
})

export const kitchenShelf = definePlaceable<{ rattled: number }>({
  id: id('cook_shelf'),
  devName: 'Kitchen shelf',
  category: 'kitchen',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'wallCream', 'wallMint'],
  tags: ['starter', 'cooking', 'decor'],
  initialState: () => ({ rattled: 0 }),
  buildArt: (ctx) => kitchenShelfArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'jiggle', seconds: 0.6, target: 'jars' },
      { kind: 'anim', clip: 'swing', seconds: 0.8, target: 'pan' },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'sound', cue: 'plate_clink' },
      { kind: 'state', patch: { rattled: ctx.state.rattled + 1 } },
    ],
    cooldownMs: 320,
  }),
})

/** A big outdoor version, so a kid can put a kitchen in the garden if they want. */
export const cookPotOutdoor = definePlaceable<{ cooked: number }>({
  id: id('cook_camp_pot'),
  devName: 'Camp cooking pot',
  category: 'townProp',
  scale: 'exterior',
  footprint: FOOT_1x1,
  cost: 45,
  stackable: false,
  walkable: false,
  tints: ['roofTeal', 'roofRed', 'roofMustard'],
  tags: ['garden', 'cooking'],
  initialState: () => ({ cooked: 0 }),
  buildArt: (ctx) => cookPotArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'spin', seconds: 0.6, target: 'spoon' },
      { kind: 'particles', effect: 'sizzle', count: 7 },
      { kind: 'monster', action: 'cook', seconds: 1.1 },
      { kind: 'ui', panel: 'cook' },
      { kind: 'sound', cue: 'sizzle' },
      { kind: 'state', patch: { cooked: ctx.state.cooked + 1 } },
    ],
    cooldownMs: 420,
  }),
})

/** A 2x2 orchard bed for a kid who wants a proper garden. Bought, not free. */
export const orchardBed = definePlaceable<{ picked: number }>({
  id: id('cook_orchard_bed'),
  devName: 'Orchard bed',
  category: 'plant',
  scale: 'exterior',
  footprint: FOOT_2x2,
  cost: 60,
  tier: 'tier1',
  stackable: false,
  walkable: false,
  tints: ['flowerPink', 'flowerRed', 'flowerYellow'],
  tags: ['garden', 'cooking', 'source'],
  initialState: () => ({ picked: 0 }),
  buildArt: (ctx) => fruitTreeArt(ctx, ctx.tint ?? 'flowerPink'),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'shake', seconds: 0.55, target: 'crown' },
      { kind: 'particles', effect: 'petals', count: 9 },
      { kind: 'spawn', what: 'cook:pick:ing_fruit' },
      { kind: 'spawn', what: 'cook:pick:ing_berry' },
      { kind: 'sound', cue: 'leaves_rustle' },
      { kind: 'state', patch: { picked: ctx.state.picked + 1 } },
    ],
    cooldownMs: 300,
  }),
})

// ---------------------------------------------------------------------------
// registration exports — `src/content/index.ts` spreads these
// ---------------------------------------------------------------------------

export const COOKING_CONTENT = [
  fruitTree,
  vegPatch,
  berryBush,
  pantry,
  cookPot,
  cookPotOutdoor,
  orchardBed,
  kitchenWindow,
  kitchenShelf,
]

/** A kid can always grow food and always cook it, with zero coins. */
export const COOKING_FREE_IDS: PlaceableId[] = [
  fruitTree.id,
  vegPatch.id,
  berryBush.id,
  pantry.id,
  cookPot.id,
  kitchenWindow.id,
  kitchenShelf.id,
]

/** Where the garden and the kitchen are seeded on a brand new town. */
export const GARDEN_SEED = {
  /** Town cells: inside the kid's own fenced yard, clear of the west gate. */
  fruitTree: { gx: 6, gy: 0, gz: 9 },
  vegPatch: { gx: 6, gy: 0, gz: 14 },
  berryBush: { gx: 7, gy: 0, gz: 15 },
  /** House kitchen cells, beside the cooker the interiors crew already put in. */
  cookPot: { gx: 2, gy: 0, gz: 1 },
  pantry: { gx: 4, gy: 0, gz: 1 },
  /*
   * ON THE WEST WALL, WITH NOTHING IN FRONT OF THEM. rot 3 = facing east, so
   * their backs are against that wall.
   *
   * They were on the NORTH wall first, over the counter run, and `playthrough`
   * caught the shelf as a prop that does not react to a real tap. It was not the
   * handler: `__harness.tapInstance` aims at the FOOTPRINT CENTRE 40 cm off the
   * floor, which for a thing hung a metre up the wall is a point in mid-air in
   * front of it — and the ray through that point runs on and hits whatever is in
   * the same diagonal, which over the counters is always something. Measured at
   * every rotation and every zoom step: at gx 2 the cooking pot won the ray four
   * times out of four.
   *
   * The west wall's rows 2 and 4 have nothing in the +x+z diagonal at all (the
   * fridge is at 1,1 and the table at 1,4, neither of which is on it), so the
   * ray meets nothing, `pickInstance` falls to its 44 px slop and the nearest
   * origin is the prop itself. Any wall prop this crew adds later wants the same
   * check — a hung thing is not a floor-standing volume, and the harness assumes
   * one.
   */
  window: { gx: 0, gy: 0, gz: 4 },
  shelf: { gx: 0, gy: 0, gz: 2 },
} as const
